import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { _className, Button, ComponentSync, EditableItem, LL_H_C, LL_V_L, Show, StorageKey } from '@nu-art/thunderstorm-frontend/index';
import { generateArray, MUSTNeverHappenException } from '@nu-art/ts-common';
import { generatedAdvisorWizard_Data, ModuleFE_Advisor_AdvisorWizard, ModuleFE_AdvisorSession, ModuleFE_EMInterface } from '@app/advisor-frontend';
import { ICONS } from '@app/styles';
import { displayMode_FreeText, stepStatus_NotSubmitted, stepStatus_Submitted, stepStatus_Submitting } from '@app/advisor-shared';
import { ModuleFE_Inbox } from '../../../../modules/module-inbox/ModuleFE_Inbox.js';
import { ModuleFE_Spotlight } from '../../../../modules/ModuleFE_Spotlight/index.js';
import { Component_AdvisorWizard_Step } from '../Component_AdvisorWizard_Step/Component_AdvisorWizard_Step.js';
import './Component_AdvisorWizard.scss';
import { prepareSubmissionData } from './utils/prepare-submission-data.js';
import { ModuleFE_AdvisorMini } from '../../../../modules/ModuleFE_AdvisorMini.js';
import { PanelKey_Home } from '../../../../_shared.js';
import { ModuleFE_AdvisorAnalytics } from '../../../../_analytics/consts.js';
export class Component_AdvisorWizard extends ComponentSync {
    //######################### Life Cycle #########################
    __onRuntimeFindingsUpdated = (...params) => {
        this.setState({ wizardData: this.getEditable_WizardData(this.state.wizard, this.state.storage, this.props.contextId, this.props.contextType) });
    };
    deriveStateFromProps(nextProps, state) {
        const _wizard = nextProps.wizard;
        state.wizard = (typeof _wizard === 'string') ? ModuleFE_Advisor_AdvisorWizard.cache.unique(_wizard) : _wizard;
        if (!state.wizard)
            throw new MUSTNeverHappenException(`Could not get wizard for id ${_wizard}`);
        //If this is the first wizard in mount, or switching wizards
        if (state.wizard._id !== this.state?.wizard._id) {
            //Recalculate the wizard data
            state.storage = this.getWizardStorageKey(state.wizard._id, nextProps.contextId);
            state.wizardData = this.getEditable_WizardData(state.wizard, state.storage, nextProps.contextId, nextProps.contextType);
            if (nextProps.contextType === 'order') {
                state.wizardData.set('contextType', 'order');
                state.wizardData.set('contextId', nextProps.contextId);
            }
            //Reset the current step
            state.currentStepIndex = 0;
        }
        return state;
    }
    componentDidMount() {
        //Report wizard open analytics event
        ModuleFE_AdvisorAnalytics.trigger.wizard.wizard__open({
            wizardId: this.state.wizard._id,
            wizardLabel: this.state.wizard.label,
            totalStepCount: this.state.wizard.steps.length
        });
    }
    componentWillUnmount() {
        const steps = this.state.wizardData.item.stepData;
        if (!steps)
            return;
        const submittedSteps = steps.filter(step => step.status !== stepStatus_NotSubmitted);
        if (steps.length === submittedSteps.length)
            return;
        ModuleFE_AdvisorAnalytics.trigger.wizard.wizard__abandon({
            wizardId: this.state.wizard._id,
            wizardLabel: this.state.wizard.label,
            stepIndex: submittedSteps.length,
        });
    }
    //######################### Logic #########################
    getWizardStorageKey(wizardId, contextId) {
        const currentEncounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
        let key = `advisor-wizard-data.${wizardId}`;
        if (currentEncounterId)
            key += `.${currentEncounterId}`;
        if (contextId)
            key += `.${contextId}`;
        return new StorageKey(key);
    }
    getEditable_WizardData(wizard, storageKey, contextId, contextType) {
        const existing = storageKey.get();
        const data = generatedAdvisorWizard_Data(wizard, contextId, contextType, existing);
        storageKey.set(data);
        return new EditableItem(data)
            .setSaveAction(async (data) => {
            this.state.storage.set(data);
            return data;
        }).setAutoSave(true);
    }
    decrementStep = () => {
        const stepIndex = this.state.currentStepIndex - 1;
        this.setState({ currentStepIndex: stepIndex });
    };
    incrementStep = async () => {
        const stepSubmissionSuccess = await this.submitStep(this.state.currentStepIndex);
        if (!stepSubmissionSuccess)
            return this.forceUpdate();
        const nextStepIndex = this.state.currentStepIndex + 1;
        this.setState({ currentStepIndex: nextStepIndex });
    };
    submitStep = async (stepIndex) => {
        const editableSteps = this.state.wizardData.editProp('stepData', []);
        const editableStep = editableSteps.editProp(this.state.currentStepIndex, {});
        //Return here if step was submitted or in the process of submitting
        if (editableStep.item.status !== stepStatus_NotSubmitted)
            return true;
        //Check if the step has changes if the display mode is free text
        if (editableStep.item.displayMode === displayMode_FreeText) {
            const freeTextData = this.state.wizardData.item.freeTextData;
            const snippetIdsToCheck = editableStep.item.snippets?.[displayMode_FreeText] ?? [];
            //All free text snippets for this step had no changes
            if (snippetIdsToCheck.every(snippet => !freeTextData?.[snippet.snippetId].hasChanges)) {
                //Update the step status and force update to show loaders
                this.logDebug('Free text step with no changes, not submitting data');
                await editableStep.updateObj({ status: stepStatus_Submitted });
                this.state.storage.set(this.state.wizardData.item);
                return true;
            }
        }
        try {
            //Update the step status and force update to show loaders
            await editableStep.updateObj({ status: stepStatus_Submitting });
            this.forceUpdate();
            //Send API call to submit the wizard data
            const submissionData = prepareSubmissionData(this.state.wizard, this.state.wizardData.item, stepIndex);
            this.logDebug('Sending Request', submissionData);
            await ModuleFE_EMInterface.updateEncounterApis.submitWizardStep(submissionData);
            await editableStep.updateObj({ status: stepStatus_Submitted });
            this.state.storage.set(this.state.wizardData.item);
            //Report step submission analytics event
            ModuleFE_AdvisorAnalytics.trigger.wizard.wizard__submitStep({
                wizardId: this.state.wizard._id,
                wizardLabel: this.state.wizard.label,
                totalStepCount: this.state.wizard.steps.length,
                stepIndex,
            });
            return true;
        }
        catch (err) {
            this.logError(err);
            await editableStep.updateObj({ status: stepStatus_NotSubmitted });
            this.state.storage.set(this.state.wizardData.item);
            return false;
        }
    };
    submitWizard = async () => {
        //Should submit data, for now does nothing
        const stepSubmissionSuccess = await this.submitStep(this.state.currentStepIndex);
        if (!stepSubmissionSuccess)
            return;
        //Report wizard end analytics event
        ModuleFE_AdvisorAnalytics.trigger.wizard.wizard__complete({
            wizardId: this.state.wizard._id,
            wizardLabel: this.state.wizard.label
        });
        ModuleFE_Inbox.setSelectedInboxItem();
        if (ModuleFE_AdvisorMini.getIsMiniActive()) {
            ModuleFE_AdvisorMini.openPanel(PanelKey_Home);
        }
        else {
            ModuleFE_Spotlight.closeSpotlight();
        }
        this.state.storage.delete();
    };
    //######################### Render #########################
    render() {
        return _jsxs(LL_V_L, { className: 'advisor-wizard-v2', children: [this.renderStep(), this.renderControls()] });
    }
    renderStep = () => {
        const wizard = this.state.wizard;
        const stepIndex = this.state.currentStepIndex;
        const step = wizard.steps[stepIndex];
        const editableSteps = this.state.wizardData.editProp('stepData', []);
        const editableStep = editableSteps.editProp(stepIndex, {});
        return _jsx(Component_AdvisorWizard_Step
        //Key should always be the wizard id, since this component keeps track of display mode for each step.
        //changing the key should also reset the display mode array
        , { wizardStep: step, stepData: editableStep, wizardData: this.state.wizardData, hideTitle: wizard.steps.length === 1, scrollRef: this.props.scrollRef }, wizard._id);
    };
    renderStepList = () => {
        const steps = this.state.wizard.steps;
        if (steps.length <= 1)
            return;
        if (ModuleFE_AdvisorMini.getIsMiniActive())
            return this.renderStepList_Mini();
        return _jsx(LL_H_C, { className: 'advisor-wizard__step-list', children: steps.map((step, index) => {
                const className = _className('advisor-wizard__step-list__item', index === this.state.currentStepIndex && 'selected');
                return _jsxs(React.Fragment, { children: [_jsxs(LL_H_C, { className: className, children: [_jsx("div", { children: index + 1 }), _jsx("div", { children: step.label })] }, index), index !== steps.length - 1 && _jsx("div", { className: 'advisor-wizard__step-list__divider' })] }, index);
            }) });
    };
    renderStepList_Mini = () => {
        const steps = this.state.wizard.steps;
        const currentStep = steps[this.state.currentStepIndex];
        const previousStepsCount = this.state.currentStepIndex;
        const nextStepCount = steps.length - this.state.currentStepIndex - 1;
        return _jsxs(LL_H_C, { className: 'advisor-wizard__step-list-mini', children: [_jsx(LL_H_C, { className: 'advisor-wizard__step-list-mini__prev', children: generateArray(previousStepsCount, index => {
                        return _jsx("div", {}, index);
                    }) }), _jsxs(LL_H_C, { className: 'advisor-wizard__step-list-mini__current', children: [_jsx("div", { children: this.state.currentStepIndex + 1 }), _jsx("div", { children: currentStep.label })] }), _jsx(LL_H_C, { className: 'advisor-wizard__step-list-mini__next', children: generateArray(nextStepCount, index => {
                        return _jsx("div", {}, index);
                    }) })] });
    };
    renderControls = () => {
        const totalSteps = this.state.wizard.steps.length;
        const currentStepIndex = this.state.currentStepIndex;
        const submitting = this.state.wizardData.item.stepData?.[this.state.currentStepIndex].status === stepStatus_Submitting;
        return _jsx(LL_H_C, { className: 'advisor-wizard__controls', children: _jsxs(Show, { children: [_jsx(Show.If, { condition: true, children: _jsxs(Button, { variant: 'wizard-nav-backward', onClick: this.decrementStep, disabled: currentStepIndex === 0, children: [_jsx(ICONS.advisor_v5_arrow.component, {}), " Back"] }) }), _jsx(Show.If, { condition: true, children: this.renderStepList() }), _jsx(Show.If, { condition: currentStepIndex !== totalSteps - 1, children: _jsxs(Button, { variant: 'wizard-nav-forward', onClick: this.incrementStep, actionInProgress: submitting, children: ["Next ", _jsx(ICONS.advisor_v5_arrow.component, {})] }) }), _jsx(Show.If, { condition: currentStepIndex === totalSteps - 1, children: _jsx(Button, { variant: 'primary', onClick: this.submitWizard, actionInProgress: submitting, style: { justifySelf: 'end' }, children: "Done" }) })] }) });
    };
}
//# sourceMappingURL=Component_AdvisorWizard.js.map